import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import leaveService from "../../services/leaveService";
import { getErrorMessage } from "../../utils/errorHandler";

import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";
import LeaveRequestTable from "../../components/common/LeaveRequestTable";

import {
  Plus,
  ClipboardList,
  ShieldAlert,
  Edit3,
  X,
  Save,
  Upload,
  FileText,
  Trash2,
  AlertTriangle,
  Ban,
  CalendarDays,
  Loader2,
} from "lucide-react";

import "../../styles/dashboard.css";

const MyLeaveRequests = () => {
  const { user } = useAuth();
  
  const navigate = useNavigate();
  const currentRole = String(user?.role || "").toLowerCase();
  // ============================================================
  // LISTE DES DEMANDES
  // ============================================================

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // ============================================================
  // TYPES DE CONGÉ
  // ============================================================

  const [leaveTypes, setLeaveTypes] = useState([]);

  // ============================================================
  // MODIFICATION
  // ============================================================

  const [editingRequest, setEditingRequest] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [editForm, setEditForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    day_part: "FULL_DAY",
    reason: "",
  });

  // Pièce jointe
  const [attachment, setAttachment] = useState(null);

  // ============================================================
  // ANNULATION
  // ============================================================

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // ============================================================
  // FORMAT ERREUR
  // ============================================================

  const formatError = (err) => {
    if (!err) {
      return "Une erreur est survenue.";
    }

    const detail = err.response?.data?.detail;

    // FastAPI / Pydantic
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (typeof item === "string") {
            return item;
          }

          if (item?.msg) {
            const location = Array.isArray(item.loc)
              ? item.loc.join(" → ")
              : "";

            return location
              ? `${location} : ${item.msg}`
              : item.msg;
          }

          return JSON.stringify(item);
        })
        .join(" | ");
    }

    if (typeof detail === "string") {
      return detail;
    }

    if (detail && typeof detail === "object") {
      return detail.msg || JSON.stringify(detail);
    }

    const message = getErrorMessage(err);

    if (typeof message === "string") {
      return message;
    }

    return "Une erreur est survenue.";
  };

  // ============================================================
  // DATE AU FORMAT YYYY-MM-DD
  // ============================================================

  const formatDateForInput = (value) => {
    if (!value) {
      return "";
    }

    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}/.test(value)
    ) {
      return value.substring(0, 10);
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ============================================================
  // DATE DU JOUR
  // ============================================================

  const getToday = () => {
    const date = new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // ============================================================
  // DEMANDE MODIFIABLE / ANNULABLE
  // ============================================================

  const isRequestEditable = (request) => {
    if (!request) {
      return false;
    }

    const status = String(request.status || "").toUpperCase();

    const allowedStatuses = [
      "PENDING_MANAGER",
      "PENDING_HR",
    ];

    if (!allowedStatuses.includes(status)) {
      return false;
    }

    const startDate =
      request.start_date ||
      request.startDate ||
      "";

    if (!startDate) {
      return false;
    }

    return formatDateForInput(startDate) > getToday();
  };

  const canEdit = isRequestEditable;
  const canCancel = isRequestEditable;

  // ============================================================
  // CHARGER LES DEMANDES
  // ============================================================

  const fetchRequests = useCallback(async () => {
    try {
      setError("");

      const data = await leaveService.getLeaveRequestsMe();

      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "Erreur lors de la récupération des demandes :",
        err
      );

      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================
  // CHARGER LES TYPES
  // ============================================================

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const data = await leaveService.getLeaveTypes();

      setLeaveTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "Erreur lors de la récupération des types de congé :",
        err
      );
    }
  }, []);

  // ============================================================
  // CHARGEMENT INITIAL
  // ============================================================

  useEffect(() => {
    fetchRequests();
    fetchLeaveTypes();
  }, [fetchRequests, fetchLeaveTypes]);

  // ============================================================
  // OUVRIR MODIFICATION
  // ============================================================

  const openEditModal = async (requestId) => {
    setError("");
    setSuccessMessage("");

    const request = requests.find(
      (item) => Number(item.id) === Number(requestId)
    );

    if (!request) {
      setError("La demande de congé est introuvable.");
      return;
    }

    // Sécurité frontend
    if (!canEdit(request)) {
      setError(
        "Cette demande ne peut plus être modifiée."
      );
      return;
    }

    console.log(
      "Demande sélectionnée pour modification :",
      request
    );

    setEditingRequest(request);

    // ----------------------------------------------------------
    // TYPE
    // ----------------------------------------------------------

    const typeId =
      request.leave_type_id ??
      request.leaveTypeId ??
      request.leave_type?.id ??
      request.type_id ??
      "";

    // ----------------------------------------------------------
    // DATES
    // ----------------------------------------------------------

    const startDate =
      request.start_date ||
      request.startDate ||
      "";

    const endDate =
      request.end_date ||
      request.endDate ||
      "";

    // ----------------------------------------------------------
    // PÉRIODE
    // ----------------------------------------------------------

    const dayPart =
      request.day_part ||
      request.dayPart ||
      "FULL_DAY";

    // ----------------------------------------------------------
    // MOTIF
    // ----------------------------------------------------------

    const reason =
      request.reason ||
      request.motif ||
      "";

    setEditForm({
      leave_type_id: String(typeId),
      start_date: formatDateForInput(startDate),
      end_date: formatDateForInput(endDate),
      day_part: dayPart,
      reason: reason,
    });

    // Important :
    // on ne renvoie pas automatiquement l'ancien fichier.
    // L'utilisateur peut choisir un nouveau fichier.
    setAttachment(null);

    if (leaveTypes.length === 0) {
      await fetchLeaveTypes();
    }

    setShowEditModal(true);
  };

  // ============================================================
  // FERMER MODAL MODIFICATION
  // ============================================================

  const closeEditModal = () => {
    if (savingEdit) {
      return;
    }

    setShowEditModal(false);
    setEditingRequest(null);
    setAttachment(null);

    setEditForm({
      leave_type_id: "",
      start_date: "",
      end_date: "",
      day_part: "FULL_DAY",
      reason: "",
    });
  };

  // ============================================================
  // CHANGEMENT FORMULAIRE
  // ============================================================

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  };

  // ============================================================
  // TYPE SÉLECTIONNÉ
  // ============================================================

  const selectedType = leaveTypes.find(
    (type) =>
      String(type.id) === String(editForm.leave_type_id)
  );

  // ============================================================
  // JUSTIFICATIF REQUIS
  // Même logique que CreateLeaveRequest
  // ============================================================

  const requiresAttachment = (code) => {
    if (!code) {
      return false;
    }

    const normalizedCode = String(code).toUpperCase();

    return [
      "MALADIE",
      "SICK",
      "SICK_LEAVE",
      "MATERNITE",
      "MATERNITY",
      "PATERNITE",
      "PATERNITY",
      "DECES",
      "DEATH",
      "MARIAGE",
      "MARRIAGE",
    ].includes(normalizedCode);
  };

  // ============================================================
  // PIÈCE JOINTE
  // EXACTEMENT COMME LA CRÉATION
  // ============================================================

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setError("");

    // ----------------------------------------------------------
    // TYPES AUTORISÉS
    // ----------------------------------------------------------

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError(
        "Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG"
      );

      e.target.value = "";
      return;
    }

    // ----------------------------------------------------------
    // MAX 5 MB
    // ----------------------------------------------------------

    if (file.size > 5 * 1024 * 1024) {
      setError(
        "La taille du fichier ne doit pas dépasser 5 Mo"
      );

      e.target.value = "";
      return;
    }

    setAttachment(file);
  };

  // ============================================================
  // SUPPRIMER PIÈCE JOINTE
  // ============================================================

  const removeAttachment = () => {
    setAttachment(null);

    const input = document.getElementById(
      "edit-attachment"
    );

    if (input) {
      input.value = "";
    }
  };

  // ============================================================
  // SOUMETTRE MODIFICATION
  // ============================================================

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    if (!editingRequest) {
      setError("Aucune demande sélectionnée.");
      return;
    }

    // ----------------------------------------------------------
    // VÉRIFICATION STATUT + DATE
    // ----------------------------------------------------------

    if (!canEdit(editingRequest)) {
      setError(
        "Cette demande ne peut plus être modifiée."
      );
      return;
    }

    // ----------------------------------------------------------
    // VALIDATIONS
    // ----------------------------------------------------------

    if (!editForm.leave_type_id) {
      setError("Veuillez sélectionner un type de congé.");
      return;
    }

    if (!editForm.start_date) {
      setError("Veuillez sélectionner la date de début.");
      return;
    }

    if (!editForm.end_date) {
      setError("Veuillez sélectionner la date de fin.");
      return;
    }

    if (editForm.end_date < editForm.start_date) {
      setError(
        "La date de fin doit être supérieure ou égale à la date de début."
      );
      return;
    }

    // La nouvelle date doit rester dans le futur
    if (editForm.start_date <= getToday()) {
      setError(
        "La date de début doit être postérieure à aujourd'hui."
      );
      return;
    }

    if (!editForm.reason.trim()) {
      setError(
        "Veuillez saisir le motif de la demande."
      );
      return;
    }

    try {
      setSavingEdit(true);

      // --------------------------------------------------------
      // APPEL SERVICE
      // La pièce jointe est envoyée comme dans la création.
      // --------------------------------------------------------

      await leaveService.employeeEditRequest(
        editingRequest.id,
        {
          leave_type_id: Number(editForm.leave_type_id),
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          day_part: editForm.day_part || "FULL_DAY",
          reason: editForm.reason.trim(),
        },
        attachment
      );

      // --------------------------------------------------------
      // SUCCÈS
      // --------------------------------------------------------

      closeEditModal();

      setSuccessMessage(
        "Votre demande de congé a été modifiée avec succès."
      );

      await fetchRequests();

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } catch (err) {
      console.error(
        "Erreur lors de la modification :",
        err
      );

      console.error(
        "Réponse backend :",
        err.response?.data
      );

      setError(formatError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  // ============================================================
  // OUVRIR MODAL ANNULATION
  // ============================================================

  const openCancelModal = (requestId) => {
    setError("");
    setSuccessMessage("");

    const request = requests.find(
      (item) => Number(item.id) === Number(requestId)
    );

    if (!request) {
      setError("La demande de congé est introuvable.");
      return;
    }

    if (!canCancel(request)) {
      setError(
        "Cette demande ne peut plus être annulée."
      );
      return;
    }

    setRequestToCancel(request);
    setShowCancelModal(true);
  };

  // ============================================================
  // FERMER MODAL ANNULATION
  // ============================================================

  const closeCancelModal = () => {
    if (cancelling) {
      return;
    }

    setShowCancelModal(false);
    setRequestToCancel(null);
  };

  // ============================================================
  // CONFIRMER ANNULATION
  // ============================================================

  const confirmCancellation = async () => {
    if (!requestToCancel) {
      return;
    }

    if (!canCancel(requestToCancel)) {
      setError(
        "Cette demande ne peut plus être annulée."
      );
      closeCancelModal();
      return;
    }

    try {
      setCancelling(true);
      setError("");
      setSuccessMessage("");

      await leaveService.cancelLeaveRequest(
        requestToCancel.id
      );

      closeCancelModal();

      setSuccessMessage(
        "La demande de congé a été annulée avec succès."
      );

      await fetchRequests();

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    } catch (err) {
      console.error(
        "Erreur lors de l'annulation :",
        err
      );

      setError(formatError(err));
    } finally {
      setCancelling(false);
    }
  };

  // ============================================================
  // ACTIONS DU TABLEAU
  // ============================================================

  const handleAction = async (requestId, action) => {
    if (action === "Modifier") {
      await openEditModal(requestId);
      return;
    }

    if (action === "Annuler") {
      openCancelModal(requestId);
    }
  };

  // ============================================================
  // NOM DU TYPE
  // ============================================================

  const getLeaveTypeName = (request) => {
    if (request?.leave_type?.name) {
      return request.leave_type.name;
    }

    if (request?.leave_type?.label) {
      return request.leave_type.label;
    }

    if (request?.leaveType?.name) {
      return request.leaveType.name;
    }

    if (request?.leaveType?.label) {
      return request.leaveType.label;
    }

    const type = leaveTypes.find(
      (item) =>
        String(item.id) ===
        String(
          request?.leave_type_id ??
          request?.leaveTypeId
        )
    );

    return (
      type?.name ||
      type?.label ||
      type?.code ||
      "Congé"
    );
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div
        className="flex-center-all"
        style={{
          height: "100vh",
          background: "#f8fafc",
          flexDirection: "column",
        }}
      >
        <Loader2
          size={40}
          style={{
            color: "#10b981",
          }}
        />

        <p
          style={{
            marginTop: "15px",
            color: "#64748b",
            fontFamily: "sans-serif",
          }}
        >
          Récupération de l'historique...
        </p>
      </div>
    );
  }

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="dashboard-layout-container">
      <Navbar />

      <div className="dashboard-content-wrapper">
        <Sidebar />

        <main className="dashboard-main-content">

          {/* ==================================================
              HEADER
          ================================================== */}

          <div className="dashboard-welcome-header">
            <div>
              <h2 className="welcome-title">
                Mes Demandes de Congé
              </h2>

              <p className="welcome-subtitle">
                Consultez l'historique complet et le statut
                de vos demandes d'absence.
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate("/employee/leave-create")
              }
              className="btn btn-primary btn-icon-group"
            >
              <Plus size={18} />

              <span>
                Demander un congé
              </span>
            </button>
          </div>

          {/* ==================================================
              ERREUR
          ================================================== */}

          {error && (
            <div
              className="form-error-box"
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <ShieldAlert
                size={18}
                style={{
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              />

              <span>
                {error}
              </span>

              <button
                type="button"
                onClick={() => setError("")}
                style={{
                  marginLeft: "auto",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* ==================================================
              SUCCÈS
          ================================================== */}

          {successMessage && (
            <div
              style={{
                marginBottom: "20px",
                padding: "13px 16px",
                borderRadius: "8px",
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                color: "#047857",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              <span
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "#10b981",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                }}
              >
                ✓
              </span>

              {successMessage}
            </div>
          )}

          {/* ==================================================
              HISTORIQUE
          ================================================== */}

          <div className="card-inner-box">
            <div
              className="card-box-header"
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <ClipboardList
                size={22}
                style={{
                  color: "#3b82f6",
                  flexShrink: 0,
                }}
              />

              <div>
                <h3 style={{ margin: 0 }}>
                  Historique des congés
                </h3>

                <p style={{ margin: "5px 0 0 0" }}>
                  Suivi de vos demandes .
                </p>
              </div>
            </div>

            <LeaveRequestTable
              requests={requests}
           //   role="employee"
              onAction={handleAction}
              onUpdated={fetchRequests}
            />
          </div>
        </main>
      </div>

      {/* ========================================================
          MODAL MODIFICATION
      ======================================================== */}

      {showEditModal && editingRequest && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(3px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeEditModal();
            }
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "600px",
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow:
                "0 25px 60px rgba(0,0,0,0.25)",
            }}
            onMouseDown={(e) =>
              e.stopPropagation()
            }
          >
            {/* HEADER */}

            <div
              style={{
                padding: "20px",
                borderBottom:
                  "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: "#fef3c7",
                    color: "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Edit3 size={20} />
                </div>

                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      color: "#0f172a",
                    }}
                  >
                    Modifier la demande
                  </h3>

                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    Modifiez les informations de votre
                    demande de congé.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeEditModal}
                disabled={savingEdit}
                style={{
                  width: "32px",
                  height: "32px",
                  border: "none",
                  borderRadius: "8px",
                  background: "#f1f5f9",
                  color: "#64748b",
                  cursor: savingEdit
                    ? "not-allowed"
                    : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* FORMULAIRE */}

            <form onSubmit={handleEditSubmit}>
              <div style={{ padding: "20px" }}>

                {/* TYPE */}

                <div
                  className="form-group"
                  style={{ marginBottom: "18px" }}
                >
                  <label
                    className="form-label"
                    style={{
                      display: "block",
                      marginBottom: "7px",
                    }}
                  >
                    Type de congé{" "}
                    <span style={{ color: "#ef4444" }}>
                      *
                    </span>
                  </label>

                  <select
                    name="leave_type_id"
                    value={editForm.leave_type_id}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                    required
                    style={{
                      width: "100%",
                      height: "40px",
                      padding: "0 12px",
                      border:
                        "1px solid #cbd5e1",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#1e293b",
                    }}
                  >
                    <option value="">
                      Sélectionner un type de congé
                    </option>

                    {leaveTypes.map((type) => (
                      <option
                        key={type.id}
                        value={type.id}
                      >
                        {type.name ||
                          type.label ||
                          type.code}
                      </option>
                    ))}
                  </select>

                  {selectedType?.description && (
                    <p
                      style={{
                        margin:
                          "6px 0 0",
                        fontSize: "12px",
                        color: "#64748b",
                      }}
                    >
                      {selectedType.description}
                    </p>
                  )}
                </div>

                {/* DATES */}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1fr 1fr",
                    gap: "12px",
                    marginBottom: "18px",
                  }}
                >
                  <div>
                    <label
                      className="form-label"
                      style={{
                        display: "block",
                        marginBottom: "7px",
                      }}
                    >
                      Date de début{" "}
                      <span
                        style={{
                          color: "#ef4444",
                        }}
                      >
                        *
                      </span>
                    </label>

                    <div
                      style={{
                        position:
                          "relative",
                      }}
                    >
                      <CalendarDays
                        size={16}
                        style={{
                          position:
                            "absolute",
                          left: "11px",
                          top: "12px",
                          color: "#64748b",
                        }}
                      />

                      <input
                        type="date"
                        name="start_date"
                        value={
                          editForm.start_date
                        }
                        min={getToday()}
                        onChange={
                          handleEditChange
                        }
                        disabled={savingEdit}
                        required
                        style={{
                          width: "100%",
                          height: "40px",
                          padding:
                            "0 10px 0 35px",
                          border:
                            "1px solid #cbd5e1",
                          borderRadius: "8px",
                          boxSizing:
                            "border-box",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className="form-label"
                      style={{
                        display: "block",
                        marginBottom: "7px",
                      }}
                    >
                      Date de fin{" "}
                      <span
                        style={{
                          color: "#ef4444",
                        }}
                      >
                        *
                      </span>
                    </label>

                    <div
                      style={{
                        position:
                          "relative",
                      }}
                    >
                      <CalendarDays
                        size={16}
                        style={{
                          position:
                            "absolute",
                          left: "11px",
                          top: "12px",
                          color: "#64748b",
                        }}
                      />

                      <input
                        type="date"
                        name="end_date"
                        value={
                          editForm.end_date
                        }
                        min={
                          editForm.start_date ||
                          getToday()
                        }
                        onChange={
                          handleEditChange
                        }
                        disabled={savingEdit}
                        required
                        style={{
                          width: "100%",
                          height: "40px",
                          padding:
                            "0 10px 0 35px",
                          border:
                            "1px solid #cbd5e1",
                          borderRadius: "8px",
                          boxSizing:
                            "border-box",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* PÉRIODE */}

                <div
                  style={{
                    marginBottom: "18px",
                  }}
                >
                  <label
                    className="form-label"
                    style={{
                      display: "block",
                      marginBottom: "7px",
                    }}
                  >
                    Période
                  </label>

                  <select
                    name="day_part"
                    value={editForm.day_part}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                    style={{
                      width: "100%",
                      height: "40px",
                      padding: "0 12px",
                      border:
                        "1px solid #cbd5e1",
                      borderRadius: "8px",
                      background: "#fff",
                    }}
                  >
                    <option value="FULL_DAY">
                      Journée complète
                    </option>

                    <option value="MORNING">
                      Matin
                    </option>

                    <option value="AFTERNOON">
                      Après-midi
                    </option>
                  </select>
                </div>

                {/* MOTIF */}

                <div
                  className="form-group"
                  style={{
                    marginBottom: "18px",
                  }}
                >
                  <label
                    className="form-label"
                    style={{
                      display: "block",
                      marginBottom: "7px",
                    }}
                  >
                    Motif de la demande{" "}
                    <span
                      style={{
                        color: "#ef4444",
                      }}
                    >
                      *
                    </span>
                  </label>

                  <textarea
                    name="reason"
                    value={editForm.reason}
                    onChange={handleEditChange}
                    disabled={savingEdit}
                    required
                    rows={4}
                    placeholder="Expliquez brièvement la raison de votre congé..."
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border:
                        "1px solid #cbd5e1",
                      borderRadius: "8px",
                      resize: "vertical",
                      boxSizing:
                        "border-box",
                    }}
                  />
                </div>

                {/* ==================================================
                    PIÈCE JOINTE
                    MÊME ORGANISATION QUE CRÉATION
                ================================================== */}

                {selectedType &&
                  requiresAttachment(
                    selectedType.code
                  ) && (
                    <div
                      className="form-group"
                      style={{
                        marginBottom: "20px",
                      }}
                    >
                      <label
                        className="form-label"
                        style={{
                          display: "block",
                          marginBottom: "7px",
                        }}
                      >
                        Pièce jointe{" "}
                        <span
                          style={{
                            fontWeight:
                              "normal",
                            color:
                              "#64748b",
                          }}
                        >
                          (Facultatif)
                        </span>
                      </label>

                      <div
                        style={{
                          border:
                            "1px dashed #cbd5e1",
                          borderRadius: "6px",
                          padding: "20px",
                          textAlign:
                            "center",
                          background:
                            "#f8fafc",
                        }}
                      >
                        {attachment ? (
                          <div
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "space-between",
                              background:
                                "#fff",
                              padding:
                                "10px 15px",
                              borderRadius:
                                "4px",
                              border:
                                "1px solid #e2e8f0",
                            }}
                          >
                            <div
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                gap: "10px",
                                minWidth: 0,
                              }}
                            >
                              <FileText
                                size={18}
                                style={{
                                  color:
                                    "#10b981",
                                  flexShrink: 0,
                                }}
                              />

                              <span
                                style={{
                                  fontSize:
                                    "14px",
                                  overflow:
                                    "hidden",
                                  textOverflow:
                                    "ellipsis",
                                  whiteSpace:
                                    "nowrap",
                                }}
                              >
                                {attachment.name}
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={
                                removeAttachment
                              }
                              disabled={
                                savingEdit
                              }
                              title="Supprimer le fichier"
                              style={{
                                background:
                                  "none",
                                border:
                                  "none",
                                cursor:
                                  savingEdit
                                    ? "not-allowed"
                                    : "pointer",
                                color:
                                  "#ef4444",
                                padding:
                                  "4px",
                                flexShrink: 0,
                              }}
                            >
                              <Trash2
                                size={18}
                              />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              id="edit-attachment"
                              onChange={
                                handleAttachmentChange
                              }
                              accept=".pdf,.jpg,.jpeg,.png"
                              style={{
                                display:
                                  "none",
                              }}
                              disabled={
                                savingEdit
                              }
                            />

                            <label
                              htmlFor="edit-attachment"
                              style={{
                                display:
                                  "inline-flex",
                                alignItems:
                                  "center",
                                gap: "8px",
                                padding:
                                  "10px 20px",
                                background:
                                  "#10b981",
                                color:
                                  "white",
                                borderRadius:
                                  "6px",
                                cursor:
                                  savingEdit
                                    ? "not-allowed"
                                    : "pointer",
                                fontSize:
                                  "14px",
                                fontWeight:
                                  "500",
                              }}
                            >
                              <Upload
                                size={16}
                              />

                              Choisir un fichier
                            </label>

                            <p
                              style={{
                                marginTop:
                                  "10px",
                                fontSize:
                                  "12px",
                                color:
                                  "#64748b",
                              }}
                            >
                              Formats acceptés :
                              PDF, JPG, JPEG,
                              PNG (max 5 Mo)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                {/* BOUTONS */}

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "flex-end",
                    gap: "12px",
                    marginTop: "25px",
                    borderTop:
                      "1px solid #e2e8f0",
                    paddingTop: "20px",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeEditModal}
                    disabled={savingEdit}
                    className="btn btn-outline"
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    disabled={savingEdit}
                    className="btn btn-primary btn-icon-group"
                    style={{
                      background:
                        "#10b981",
                      border: "none",
                    }}
                  >
                    {savingEdit ? (
                      <>
                        <Loader2
                          size={17}
                        />
                        <span>
                          Enregistrement...
                        </span>
                      </>
                    ) : (
                      <>
                        <Save size={17} />
                        <span>
                          Enregistrer
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================
          MODAL ANNULATION
      ======================================================== */}

      {showCancelModal &&
        requestToCancel && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background:
                "rgba(15, 23, 42, 0.65)",
              display: "flex",
              alignItems: "center",
              justifyContent:
                "center",
              zIndex: 10000,
              padding: "20px",
            }}
            onMouseDown={(e) => {
              if (
                e.target ===
                e.currentTarget
              ) {
                closeCancelModal();
              }
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "440px",
                background: "#fff",
                borderRadius:
                  "16px",
                boxShadow:
                  "0 25px 60px rgba(0,0,0,0.25)",
                padding: "25px",
              }}
              onMouseDown={(e) =>
                e.stopPropagation()
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: "12px",
                  marginBottom:
                    "18px",
                }}
              >
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius:
                      "10px",
                    background:
                      "#fee2e2",
                    color:
                      "#dc2626",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                  }}
                >
                  <Ban size={21} />
                </div>

                <div>
                  <h3
                    style={{
                      margin: 0,
                      color:
                        "#0f172a",
                    }}
                  >
                    Annuler la demande
                  </h3>

                  <p
                    style={{
                      margin:
                        "4px 0 0",
                      fontSize:
                        "12px",
                      color:
                        "#64748b",
                    }}
                  >
                    Cette action est irréversible.
                  </p>
                </div>
              </div>

              <div
                style={{
                  padding:
                    "14px",
                  background:
                    "#f8fafc",
                  borderRadius:
                    "8px",
                  marginBottom:
                    "18px",
                }}
              >
                <strong>
                  {getLeaveTypeName(
                    requestToCancel
                  )}
                </strong>

                <div
                  style={{
                    marginTop:
                      "6px",
                    fontSize:
                      "13px",
                    color:
                      "#64748b",
                  }}
                >
                  Du{" "}
                  {formatDateForInput(
                    requestToCancel.start_date
                  )}{" "}
                  au{" "}
                  {formatDateForInput(
                    requestToCancel.end_date
                  )}
                </div>
              </div>

              <p
                style={{
                  color:
                    "#475569",
                  fontSize:
                    "14px",
                  lineHeight:
                    "1.5",
                  marginBottom:
                    "22px",
                }}
              >
                Voulez-vous vraiment annuler
                cette demande de congé ?
              </p>

              <div
                style={{
                  display:
                    "flex",
                  justifyContent:
                    "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={
                    closeCancelModal
                  }
                  disabled={
                    cancelling
                  }
                  className="btn btn-outline"
                >
                  Non, garder
                </button>

                <button
                  type="button"
                  onClick={
                    confirmCancellation
                  }
                  disabled={
                    cancelling
                  }
                  className="btn btn-primary btn-icon-group"
                  style={{
                    background:
                      "#ef4444",
                    border:
                      "none",
                  }}
                >
                  {cancelling ? (
                    <>
                      <Loader2
                        size={17}
                      />
                      <span>
                        Annulation...
                      </span>
                    </>
                  ) : (
                    <>
                      <Ban
                        size={17}
                      />
                      <span>
                        Oui, annuler
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default MyLeaveRequests;